import { CalendarDaysIcon, Cog6ToothIcon, HeroIconProps, SquaresPlusIcon, TvIcon } from 'components'
import { useWorkspace } from 'components/providers'
import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useLimitedResourceCounts, usePermission, useShowDevelopmentFeatures } from 'utils'
import { isContentPage, isSettingsPage, useCurrentPage } from '../utils/pageUtils'
import { getEventsInSections, useEvents } from './eventUtils'
import { RouteName, RoutePath } from './routing'

export interface SideMenuItem {
  text: string
  page: RoutePath
  LeftIcon?: (props: HeroIconProps) => ReactNode
  isDisabled?: boolean
  isCurrent?: boolean
  subItems?: SideMenuItem[]
  statusDot?: 'warning' | 'danger'
}

export const useSideMenuItems = (): SideMenuItem[] => {
  const currentPage = useCurrentPage()
  const permission = usePermission()
  const [showDevelopmentFeatures] = useShowDevelopmentFeatures()
  const { workspace } = useWorkspace()
  const { events = [] } = useEvents(workspace?.id)
  const { live, upcoming, past } = getEventsInSections(events)
  const location = useLocation()
  const { limitCounts, warningCounts } = useLimitedResourceCounts()

  const currentSection = currentPage?.rootPath || currentPage?.parentPath || currentPage?.path

  return [
    {
      text: 'Events',
      page: RoutePath.Events,
      LeftIcon: CalendarDaysIcon,
      isCurrent: currentSection === RouteName.Events,
      subItems: [
        {
          text: 'Live',
          page: (RoutePath.Events + '#live') as RoutePath,
          isDisabled: !live.length,
          isCurrent: location.hash === '#live',
        },
        {
          text: 'Upcoming',
          page: (RoutePath.Events + '#upcoming') as RoutePath,
          isDisabled: !upcoming.length,
          isCurrent: location.hash === '#upcoming',
        },
        {
          text: 'Past',
          page: (RoutePath.Events + '#past') as RoutePath,
          isDisabled: !past.length,
          isCurrent: location.hash === '#past',
        },
      ],
    },
    {
      text: 'Facilities',
      page: RoutePath.Facilities,
      LeftIcon: TvIcon,
      isCurrent: currentSection === RouteName.Facilities,
      isDisabled: !showDevelopmentFeatures,
    },
    {
      text: 'Content',
      page: RoutePath.Text,
      LeftIcon: SquaresPlusIcon,
      isCurrent: isContentPage(currentSection),
      isDisabled: !showDevelopmentFeatures,
      subItems: [
        {
          text: 'Templates',
          page: RoutePath.Text,
          isDisabled: true,
        },
        {
          text: 'Traits',
          page: RoutePath.Text,
          isDisabled: true,
        },
        {
          text: 'Text',
          page: RoutePath.Text,
          isCurrent: currentSection === RouteName.Text,
        },
        {
          text: 'Media',
          page: RoutePath.ManageMedia,
          isCurrent: currentSection === RouteName.Media,
        },
      ],
    },
    // {
    //   text: 'Analytics',
    //   page: RoutePath.Workspace,
    //   LeftIcon: ChartBarIcon,
    //   isDisabled: true,
    // },
    ...(permission.workspace === 'admin'
      ? [
          {
            text: 'Settings',
            page: RoutePath.ManageWorkspace,
            LeftIcon: Cog6ToothIcon,
            isCurrent: isSettingsPage(currentSection),
            subItems: [
              {
                text: 'Workspace',
                page: RoutePath.ManageWorkspace,
                isCurrent: currentSection === RouteName.ManageWorkspace,
              },
              {
                text: 'Members',
                page: RoutePath.ManageWorkspaceUsers,
                isCurrent: currentSection === RouteName.ManageWorkspaceUsers,
              },
              {
                text: 'Integrations',
                page: RoutePath.Text,
                isDisabled: true,
              },
              {
                text: 'Intelligence',
                page: RoutePath.Intelligence,
                isCurrent: currentSection === RouteName.Intelligence,
                isDisabled: !showDevelopmentFeatures,
              },
              {
                text: 'Subscription',
                page: RoutePath.Subscription,
                isCurrent: currentSection === RouteName.Subscription,
                isDisabled: !showDevelopmentFeatures,
                statusDot: limitCounts
                  ? ('danger' as const)
                  : warningCounts
                  ? ('warning' as const)
                  : undefined,
              },
            ],
          },
        ]
      : []),
  ]
}
